/**
 * Sidebar navigation tree.
 *
 * Building a `navGroups` from these factories keeps the render component
 * tiny and lets us unit-test the structure independently. Icons are imported
 * lazily by the consumer to avoid bundle bloat when the sidebar is hidden.
 */
import {
  Home, MessageCircle, Layers, TrendingUp, Users as UsersIcon,
  Crown, BookOpen, HelpCircle, Settings, Heart,
} from 'lucide-react';

export const ALL_NAV_GROUPS = [
  { id: 'home', icon: Home, label: 'Home', path: '/dashboard', type: 'link' },
  {
    id: 'ava',
    icon: MessageCircle,
    label: 'AvA',
    type: 'group',
    children: [
      { label: 'My Practice Sessions', path: '/conversations' },
    ],
  },
  {
    id: 'apps',
    icon: Layers,
    label: 'Apps',
    type: 'group',
    /* Adult-mode filter: children flagged `adultOnly` render only when
       useAdultMode === true; children flagged `teenOnly` only for teens.
       Items with neither flag render for both audiences. */
    children: [
      { label: 'Real Connections',      path: '/community',          teenOnly: true },
      { label: 'Challenges',            path: '/challenges',         teenOnly: true },
      { label: 'Friend Challenges',     path: '/friend-challenges',  teenOnly: true },
      { label: 'Meetups',               path: '/meetups',            teenOnly: true },
      { label: 'Leaderboard',           path: '/leaderboard',        teenOnly: true },
      { label: 'Pods',                  path: '/accountability-pods' },
      { label: 'Mentorship',            path: '/mentorship' },
      { label: 'Celebrations',          path: '/badges',             teenOnly: true },
      { label: 'My Vault',              path: '/memes' },
      { label: 'Invite a Friend',       path: '/rewards',            teenOnly: true },
      { label: 'Social Quests',         path: '/quests',             teenOnly: true },
      { label: 'NFT Trading',           path: '/nft-marketplace' },
      { label: 'Marketplace',           path: '/marketplace' },
      // Phase D — adult-only modules surfaced when isAdultMode is true.
      { label: 'Career Playbook',       path: '/playbook',           adultOnly: true },
      { label: 'Negotiation Simulator', path: '/role-play?track=wealth', adultOnly: true },
      { label: 'Tone Analytics',        path: '/achievements?view=tone', adultOnly: true },
      { label: 'Boundary Lab',          path: '/role-play?track=health', adultOnly: true },
    ],
  },
  {
    id: 'progress',
    icon: TrendingUp,
    label: 'Progress',
    type: 'group',
    children: [
      { label: 'My Readiness',       path: '/wellness' },
      { label: 'Skill Tree',         path: '/skill-tree' },
      { label: 'Growth Journal',     path: '/growth-journal' },
      { label: 'Role-Play',          path: '/role-play' },
      { label: 'Confidence Tracker', path: '/achievements' },
      { label: 'Growth Story',       path: '/growth-story' },
      { label: 'Mood Playlist',      path: '/mood-playlist' },
      { label: 'Weekly Report',      path: '/weekly-report' },
      { label: 'My Profile',         path: '/profile' },
    ],
  },
  {
    id: 'family',
    icon: UsersIcon,
    label: 'Family',
    type: 'group',
    children: [
      { label: 'Charter Doc',  path: '/charter' },
    ],
  },
];

// Hearts Store sits in the bottom nav next to "Upgrade Plan" — it's a
// commerce shortcut (top-up extra practice time) rather than a content
// section, so it belongs alongside the other purchase / settings entries
// rather than inside the activity groups above.
export const BOTTOM_LINKS = [
  { icon: Crown,      label: 'Upgrade Plan',    path: '/pricing', highlight: true },
  { icon: Heart,      label: 'Hearts Store',    path: '/hearts' },
  { icon: BookOpen,   label: 'Docs',            path: '/dashboard/docs' },
  { icon: HelpCircle, label: 'Help & Support',  path: '/help' },
  { icon: Settings,   label: 'Settings',        path: '/settings' },
];

// Sprint 208 Master item #5 — Parent portal nav is INTENTIONALLY LEAN.
// Only five links + the Privacy Shield panel. Everything else (Apps,
// Progress menu, AvA chat, etc.) is hidden because the parent dashboard
// surfaces signal, not surface area.
export const PARENT_NAV_GROUPS = [
  { id: 'home', icon: Home, label: 'Dashboard', path: '/parent-dashboard', type: 'link' },
];

export const PARENT_BOTTOM_LINKS = [
  { icon: Crown,      label: 'Upgrade Plan',    path: '/pricing', highlight: true },
  { icon: Heart,      label: 'Hearts Store',    path: '/hearts' },
  { icon: BookOpen,   label: 'Docs',            path: '/charter',
    description: 'Signed family charters & history' },
  { icon: HelpCircle, label: 'Help & Support',  path: '/help' },
  { icon: TrendingUp, label: 'Progress',        path: '/parent-dashboard#progress',
    description: 'Jump to challenge ticker + countdown' },
];
